/**
 * Аналитики, учёт времени и отчёты.
 *
 * Аналитика — не поле, а строка факта: у неё есть автор, дата и много строк
 * на одну задачу. Поэтому и в интерфейсе это таблица, а не набор полей.
 */

import { downloadFile, request } from "@/shared/api/client";
import type { Condition } from "@/shared/api/views";

export interface Analytic {
  id: number;
  code: string;
  name: string;
  group_name: string | null;
  is_system: boolean;
  is_active: boolean;
  can_edit_others: boolean;
  allowed_statuses: number[];
  position: number;
}

export interface AnalyticField {
  id: number;
  code: string;
  name: string;
  type: string;
  config: Record<string, unknown>;
  is_required: boolean;
  is_multi: boolean;
  position: number;
}

export interface AnalyticEntry {
  id: number;
  analytic_id: number;
  task_id: number;
  comment_id: number | null;
  entry_date: string;
  author_id: number;
  values: Record<string, unknown>;
  created_at: string;
  updated_at: string | null;
  edited_by_id: number | null;
}

/** Колонки источника: у аналитик они известны только вместе с выбранной. */
export function reportSourceColumns(
  source: string,
  analyticId?: number,
): Promise<ReportSourceColumn[]> {
  const tail = analyticId === undefined ? "" : `?analytic_id=${String(analyticId)}`;
  return request<ReportSourceColumn[]>(`/api/reports/sources/${source}/columns${tail}`);
}

export function listAnalytics(includeInactive = false): Promise<Analytic[]> {
  return request<Analytic[]>(`/api/analytics?include_inactive=${String(includeInactive)}`);
}

export function analyticFields(analyticId: number): Promise<AnalyticField[]> {
  return request<AnalyticField[]>(`/api/analytics/${analyticId}/fields`);
}

export function installStandardAnalytics(): Promise<{ created: number }> {
  return request<{ created: number }>("/api/analytics/standard", { method: "POST" });
}

export function createAnalytic(body: {
  code: string;
  name: string;
  group_name?: string | null;
  can_edit_others?: boolean;
}): Promise<Analytic> {
  return request<Analytic>("/api/analytics", { method: "POST", body });
}

export function updateAnalytic(
  analyticId: number,
  body: {
    name?: string;
    group_name?: string | null;
    is_active?: boolean;
    can_edit_others?: boolean;
    position?: number;
  },
): Promise<Analytic> {
  return request<Analytic>(`/api/analytics/${analyticId}`, { method: "PATCH", body });
}

export function deleteAnalytic(analyticId: number): Promise<void> {
  return request<undefined>(`/api/analytics/${analyticId}`, { method: "DELETE" });
}

export function createAnalyticField(
  analyticId: number,
  body: { code: string; name: string; type: string; is_required?: boolean },
): Promise<AnalyticField> {
  return request<AnalyticField>(`/api/analytics/${analyticId}/fields`, { method: "POST", body });
}

export function taskEntries(taskId: number, analyticId?: number): Promise<AnalyticEntry[]> {
  const query = analyticId === undefined ? "" : `?analytic_id=${String(analyticId)}`;
  return request<AnalyticEntry[]>(`/api/tasks/${taskId}/analytics${query}`);
}

export function addEntry(
  taskId: number,
  body: { analytic_id: number; values: Record<string, unknown>; entry_date?: string },
): Promise<AnalyticEntry> {
  return request<AnalyticEntry>(`/api/tasks/${taskId}/analytics`, {
    method: "POST",
    body,
  });
}

export function editEntry(
  entryId: number,
  body: { values: Record<string, unknown>; entry_date?: string },
): Promise<AnalyticEntry> {
  return request<AnalyticEntry>(`/api/analytic-entries/${entryId}`, {
    method: "PATCH",
    body,
  });
}

export function copyEntry(entryId: number): Promise<AnalyticEntry> {
  return request<AnalyticEntry>(`/api/analytic-entries/${entryId}/copy`, { method: "POST" });
}

export function deleteEntry(entryId: number): Promise<void> {
  return request<undefined>(`/api/analytic-entries/${entryId}`, { method: "DELETE" });
}

// ── Учёт времени ─────────────────────────────────────────────────────────────

export interface TimerState {
  task_id: number;
  is_running: boolean;
  seconds: number;
  hours: string;
  started_at: string | null;
  note: string | null;
}

export interface LoggedTime {
  entry_id: number;
  task_id: number;
  hours: string;
  comment_id: number | null;
}

export function timer(): Promise<TimerState | null> {
  return request<TimerState | null>("/api/timer");
}

export function startTimer(taskId: number, note?: string): Promise<TimerState> {
  return request<TimerState>("/api/timer/start", {
    method: "POST",
    body: { task_id: taskId, ...(note ? { note } : {}) },
  });
}

export function pauseTimer(): Promise<TimerState> {
  return request<TimerState>("/api/timer/pause", { method: "POST" });
}

export function resumeTimer(): Promise<TimerState> {
  return request<TimerState>("/api/timer/resume", { method: "POST" });
}

export function stopTimer(body: { work_kind?: string; note?: string } = {}): Promise<LoggedTime> {
  return request<LoggedTime>("/api/timer/stop", { method: "POST", body });
}

export function discardTimer(): Promise<void> {
  return request<undefined>("/api/timer", { method: "DELETE" });
}

export function logTime(
  taskId: number,
  body: { hours: string; work_kind?: string; note?: string; entry_date?: string },
): Promise<LoggedTime> {
  return request<LoggedTime>(`/api/tasks/${taskId}/time`, { method: "POST", body });
}

// ── Рабочий календарь ────────────────────────────────────────────────────────

export interface CalendarDay {
  day: string;
  kind: "holiday" | "workday" | "short";
  note: string | null;
}

export function workCalendar(since: string, until: string): Promise<CalendarDay[]> {
  return request<CalendarDay[]>(`/api/work-calendar?since=${since}&until=${until}`);
}

export function setWorkDay(body: CalendarDay): Promise<CalendarDay> {
  return request<CalendarDay>("/api/work-calendar", { method: "POST", body });
}

export function clearWorkDay(day: string): Promise<void> {
  return request<undefined>(`/api/work-calendar/${day}`, { method: "DELETE" });
}

export interface WorkloadRow {
  user_id: number;
  plan_hours: string;
  fact_hours: string;
  norm_hours: string;
  norm_days: number;
}

export function workload(since: string, until: string): Promise<WorkloadRow[]> {
  return request<WorkloadRow[]>(`/api/reports/workload?since=${since}&until=${until}`);
}

// ── Отчёты ───────────────────────────────────────────────────────────────────

export interface ReportSourceColumn {
  key: string;
  title: string;
  kind: string;
  groupable: boolean;
  aggregatable: boolean;
}

export interface ReportSource {
  key: string;
  title: string;
  columns: ReportSourceColumn[];
  /** Источник не построится, пока не выбрана аналитика. */
  needs_analytic: boolean;
}

export interface ReportMeasure {
  key?: string;
  aggregate: "sum" | "count" | "avg" | "min" | "max";
  title?: string;
}

export interface ReportChart {
  kind: "bar" | "line" | "pie" | "area" | "combo";
  title?: string;
  x: string;
  series: string[];
}

/** Условное форматирование: какую колонку и каким тоном подсветить. */
export interface ReportRule {
  column: string;
  op: "gt" | "gte" | "lt" | "lte" | "eq" | "ne";
  /** Порог сравнения. Сервер разбирает число и из строки: поле ввода одно. */
  value: string | number | null;
  tone: "neutral" | "accent" | "info" | "success" | "warning" | "danger";
}

export interface ReportSpec {
  filter?: Record<string, unknown> | null;
  conditions?: Condition[];
  logic?: string;
  group_by?: string[];
  measures?: ReportMeasure[];
  computed?: { title: string; formula: string }[];
  charts?: ReportChart[];
  rules?: ReportRule[];
  order_by?: string | null;
  descending?: boolean;
  limit?: number;
  params?: Record<string, unknown>;
}

export interface Report {
  id: number;
  name: string;
  group_name: string | null;
  description: string | null;
  source: string;
  spec: ReportSpec;
  scope: "personal" | "shared" | "account";
  owner_id: number;
  is_system: boolean;
  position: number;
  created_at: string;
  updated_at: string;
  can_edit: boolean;
  /** Отбор, разложенный на пронумерованные правила: их и правит мастер. */
  conditions: Condition[];
  logic: string;
}

export interface ReportResult {
  columns: { key: string; title: string; kind: string; role: string }[];
  rows: Record<string, unknown>[];
  totals: Record<string, unknown>;
  charts: {
    kind: string;
    title: string;
    labels: unknown[];
    series: { key: string; values: unknown[] }[];
  }[];
  row_count: number;
  duration_ms: number;
  run_id: number | null;
}

export interface ReportRun {
  id: number;
  report_id: number;
  user_id: number;
  status: string;
  rows: number;
  duration_ms: number;
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

export function reportSources(): Promise<ReportSource[]> {
  return request<ReportSource[]>("/api/reports/sources");
}

export function listReports(): Promise<Report[]> {
  return request<Report[]>("/api/reports");
}

export function createReport(body: {
  name: string;
  source: string;
  spec: ReportSpec;
  group_name?: string;
  description?: string;
  scope?: Report["scope"];
}): Promise<Report> {
  return request<Report>("/api/reports", { method: "POST", body });
}

export function updateReport(
  reportId: number,
  body: Partial<{
    name: string;
    source: string;
    spec: ReportSpec;
    group_name: string;
    scope: Report["scope"];
  }>,
): Promise<Report> {
  return request<Report>(`/api/reports/${reportId}`, { method: "PATCH", body });
}

export function deleteReport(reportId: number): Promise<void> {
  return request<undefined>(`/api/reports/${reportId}`, { method: "DELETE" });
}

export function runReport(
  reportId: number,
  body: { params?: Record<string, unknown>; save?: boolean } = {},
): Promise<ReportResult> {
  return request<ReportResult>(`/api/reports/${reportId}/run`, { method: "POST", body });
}

export function reportRuns(reportId: number): Promise<ReportRun[]> {
  return request<ReportRun[]>(`/api/reports/${reportId}/runs`);
}

export function reportRun(runId: number): Promise<ReportResult> {
  return request<ReportResult>(`/api/reports/runs/${runId}`);
}

export function installStandardReports(): Promise<{ created: number }> {
  return request<{ created: number }>("/api/reports/standard", { method: "POST" });
}

export function reportExportUrl(reportId: number, fmt: "xlsx" | "csv" = "xlsx"): string {
  return `/api/reports/${reportId}/export?fmt=${fmt}`;
}

export function exportReport(reportId: number, fmt: "xlsx" | "csv" = "xlsx"): Promise<void> {
  return downloadFile(reportExportUrl(reportId, fmt));
}
